import { useGetCountries } from "@/actions/hooks/useGetCountries.tsx";
import { ChevronDown } from "@/components/Icons/utility/ChevronDown.tsx";
import { CountrySelectProps } from "@/components/Inputs/CountrySelect/CountrySelectProps.ts";
import { useCountrySearch } from "@/components/Inputs/CountrySelect/useCountrySearch.ts";
import { countryDialCodes } from "@/components/Inputs/PhoneInput/countryDialCodes.ts";
import { requiredRule } from "@/helpers/form/FormRules.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";
import { Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const CountrySelect = (props: CountrySelectProps) => {
  const { t } = useTranslation();
  const { shadowRoot } = useShadowRoot();
  const { countries, getCountriesLoading } = useGetCountries();
  const { name, error } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { priorityCountries, filteredCountries } = useCountrySearch(countries, search);

  useEffect(() => {
    if (!props.watch?.(name)) {
      if (getAPIUrl().includes("campuccino")) {
        props.setValue?.(name, "DEU");
      } else {
        props.setValue?.(name, "HRV");
      }
    }

    if (countries.length && props.watch?.(name)) {
      setTimeout(async () => props.trigger?.(name));
    }
    // eslint-disable-next-line
  }, [countries, props.watch?.(name)]);

  const currentValue = props.watch?.(name) as string;
  const selectedCountryName = useMemo(() => {
    return countries.find((c) => c.iso3Code === currentValue)?.name || "";
  }, [countries, currentValue]);

  const registration = props.register?.(name, requiredRule);

  const handleClickOutside = useCallback((event: Event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSearch("");
      setFocused(false);
    }
  }, []);

  useEffect(() => {
    shadowRoot.addEventListener("mousedown", handleClickOutside);
    return () => shadowRoot.removeEventListener("mousedown", handleClickOutside);
  }, [shadowRoot, handleClickOutside]);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (iso3Code: string) => {
    props.setValue?.(name, iso3Code);
    setIsOpen(false);
    setSearch("");
    setFocused(false);
    setTimeout(() => props.trigger?.(name));
  };

  const handleToggle = () => {
    if (getCountriesLoading) return;
    setIsOpen((prev) => !prev);
    setFocused((prev) => !prev);
    if (isOpen) {
      setSearch("");
      setFocused(false);
    }
  };

  if (!registration) return null;

  const isFloating = focused || isOpen || !!currentValue;

  const rootClasses = [
    "c-floating-select",
    "c-country-select",
    isFloating && "c-floating-select--floating",
    (focused || isOpen) && "c-floating-select--focused",
    error && "c-floating-select--error",
    isOpen && "c-country-select--open",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClasses} ref={dropdownRef}>
      {/* Hidden input for react-hook-form */}
      <input type="hidden" {...registration} value={currentValue || ""} />

      <div className="c-floating-select__field c-country-select__trigger" onClick={handleToggle}>
        <span className="c-country-select__value">{selectedCountryName}</span>
      </div>
      <label className="c-floating-select__label">{t(name)}</label>
      <span className="c-floating-select__chevron c-country-select__chevron">
        <ChevronDown />
      </span>

      {isOpen && (
        <div className="c-country-select__dropdown">
          <div className="c-country-select__search-wrap">
            <Search size={14} className="c-country-select__search-icon" />
            <input
              ref={searchRef}
              type="text"
              className="c-country-select__search"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="c-country-select__options">
            {!search.trim() &&
              priorityCountries.map((country) => (
                <div
                  key={`priority-${country.iso3Code}`}
                  className={`c-country-select__option ${
                    country.iso3Code === currentValue ? "c-country-select__option--selected" : ""
                  }`}
                  onClick={() => handleSelect(country.iso3Code)}
                >
                  <span className="c-country-select__option-flag">
                    {countryDialCodes[country.iso3Code]?.flag ?? "🏳"}
                  </span>
                  {country.name}
                </div>
              ))}
            {!search.trim() && priorityCountries.length > 0 && <div className="c-country-select__divider" />}
            {filteredCountries.map((country) => (
              <div
                key={country.iso3Code}
                className={`c-country-select__option ${
                  country.iso3Code === currentValue ? "c-country-select__option--selected" : ""
                }`}
                onClick={() => handleSelect(country.iso3Code)}
              >
                <span className="c-country-select__option-flag">
                  {countryDialCodes[country.iso3Code]?.flag ?? "🏳"}
                </span>
                {country.name}
              </div>
            ))}
            {filteredCountries.length === 0 && (
              <div className="c-country-select__no-results">{t("noResults")}</div>
            )}
          </div>
        </div>
      )}

      {error && error.type !== "required" && (
        <span className="c-floating-select__error">{error.message && `${error.message}`}</span>
      )}
    </div>
  );
};
